import type { RequestOptions } from '@@/plugin-request/request';
import { getIntl, request, RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';

/** @doc https://github.com/sindresorhus/query-string#arrayformat-1 */
import queryString from 'query-string';
import { BILLING_REQUIRED_EVENT } from './constants/billing';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

const BILLING_REQUIRED_ERROR_CODES = new Set([
  'BILLING_REQUIRED',
  'ORG_BILLING_REQUIRED',
  'PAYWALL_BILLING_REQUIRED',
]);

const showBillingRequiredNotice = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(BILLING_REQUIRED_EVENT, {
      detail: {
        message: getIntl().formatMessage({
          id: 'components.billingRequiredOverlay.defaultMessage',
          defaultMessage:
            'Your organisation billing requires attention before this action can continue. Please contact your organisation admin.',
        }),
      },
    }),
  );
};

const isBillingRequiredError = (error: any) => {
  const status = error?.response?.status;
  if (status === 402) return true;

  const errorCode = String(
    error?.info?.errorCode ?? error?.response?.data?.errorCode ?? error?.response?.data?.code ?? '',
  ).toUpperCase();
  if (BILLING_REQUIRED_ERROR_CODES.has(errorCode)) return true;

  const detail = String(
    error?.info?.errorMessage ??
      error?.response?.data?.detail ??
      error?.response?.data?.errorMessage ??
      '',
  ).toLowerCase();
  return detail.includes('billing required') || detail.includes('payment required');
};

const showBusinessError = (errorInfo: ResponseStructure | undefined) => {
  if (!errorInfo) return;

  const { errorMessage, errorCode } = errorInfo;
  switch (errorInfo.showType) {
    case ErrorShowType.SILENT:
    case ErrorShowType.REDIRECT:
      // These modes do not display a global error notice.
      break;
    case ErrorShowType.WARN_MESSAGE:
      message.warning(errorMessage);
      break;
    case ErrorShowType.ERROR_MESSAGE:
      message.error(errorMessage);
      break;
    case ErrorShowType.NOTIFICATION:
      notification.open({
        description: errorMessage,
        message: errorCode,
      });
      break;
    default:
      message.error(errorMessage);
  }
};

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  paramsSerializer(params) {
    return queryString.stringify(params);
  },

  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: async (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      if (isBillingRequiredError(error)) {
        showBillingRequiredNotice();
        return;
      }

      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        showBusinessError(error.info);
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        // message.error(`Response status:${error.response.status}`);
        // Background file lookups may return Gone; do not surface a global retention toast.
        if (error.response.status === 410) return;
        if (
          localStorage.getItem('tokenRefresh') &&
          error.response.status === 401 &&
          error.config.url !== `/api/user/refresh/`
        ) {
          const res = await request(`/api/user/refresh/`, { method: 'POST' });
          localStorage.setItem('token', res.accessToken);
          return request(error.config.url, opts);
        }
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error(
          getIntl().formatMessage({
            id: 'components.appShell.noResponseError',
            defaultMessage: 'None response! Please retry.',
          }),
        );
      } else {
        // 发送请求时出了点问题
        message.error(
          getIntl().formatMessage({
            id: 'components.appShell.requestError',
            defaultMessage: 'Request error, please retry.',
          }),
        );
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (url: null | string, options: RequestOptions) => {
      if (url === `/api/user/refresh/`)
        options.headers = {
          ...options.headers,
          Authorization: `Bearer ${localStorage.getItem('tokenRefresh')}`,
        };
      else
        options.headers = {
          ...options.headers,
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        };
      return {
        url: `${url}`,
        options: {
          ...options,
          interceptors: true,
        },
      };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as unknown as ResponseStructure;
      if (response.config.url === `/api/user/refresh/`) localStorage.setItem('token', data.token);
      if (data?.success === false) {
        message.error(
          getIntl().formatMessage({
            id: 'components.appShell.requestFailedError',
            defaultMessage: 'Request failed!',
          }),
        );
      }
      return response;
    },
  ],
};
